import { Component, OnDestroy, OnInit } from '@angular/core';
// Module
import { debounceTime, lastValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { Store } from '@ngxs/store';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
// Service
import {
	BroadcastService,
	ChangeEventStatusChannelAction,
	EVENT_STATUS,
	SignalRState,
	ToasterType
} from '@cv-invision/core';
import { QualityDocumentService } from '../../../../service/document-quality.service';
// Component
import { UcdpOverrideDialogComponent } from '../ucdp-override-dialog/ucdp-override-dialog.component';
import { UCDPScoreDialogComponent } from '../ucdp-score-dialog/ucdp-score-dialog.component';
import { UcdpSubmissionErrorDialogComponent } from '../ucdp-submission-error-dialog/ucdp-submission-error-dialog.component';
// Model
import { OrderDetailModel } from '../../../../state/model/order-detail.model';
import { UcdpHardstopsDataModel, UcdpSubmissionModel, UcdpSumissionDataModel } from '../../../../model/ucdp.model';

@Component({
	selector: 'cv-ucdp-details-dialog',
	templateUrl: 'ucdp-details-dialog.component.html'
})
export class UcdpDetailsDialogComponent implements OnInit, OnDestroy {
	channels$: Observable<any> = this.store.select(SignalRState.channelEvents);
	loadData$ = new Subject();

	orderDetail!: OrderDetailModel;
	ucdpData: any = {
		submissions: new UcdpSubmissionModel(),
		findings: [],
		hardstops: []
	};
	submissionSelected: UcdpSumissionDataModel;
	loading = true;
	loadingSub = false;
	basicModal = {
		showSubmissionErrors: false,
		serviceName: '',
		message: []
	};

	get canSubmitOverrides(): boolean {
		let response = false;
		const overriden = (this.ucdpData?.hardstops || []).filter(
			(item) => item.overrideReasonCode !== null && item.autoOverride === false
		);

		if (overriden.length > 0) {
			response = true;
		}

		return response;
	}

	private atComponentDestroy = new Subject();

	constructor(
		private store: Store,
		private dialogService: DialogService,
		private dialogConfig: DynamicDialogConfig,
		private broadcastService: BroadcastService,
		private qualityService: QualityDocumentService
	) {
		this.orderDetail = this.dialogConfig.data?.orderDetail;
	}

	ngOnInit(): void {
		this.loadData();

		this.loadData$.pipe(takeUntil(this.atComponentDestroy), debounceTime(1000)).subscribe((data: any) => {
			this.loadData();
		});

		this.channels$.pipe(takeUntil(this.atComponentDestroy)).subscribe((channels: any) => {
			if (channels && channels[`orders:${this.orderDetail.id}`]) {
				channels[`orders:${this.orderDetail.id}`].events.forEach((channelEvent: any) => {
					let clearEvent = false;
					if (channelEvent.status === 'new') {
						switch (channelEvent.event) {
							case 'transaction:complete':
							case 'override:complete':
								clearEvent = true;
								this.loadData$.next(true);
								break;
						}
					}

					if (clearEvent) {
						const orderChannel = `orders:${this.orderDetail.id}`;
						this.store.dispatch(new ChangeEventStatusChannelAction(orderChannel, EVENT_STATUS.OLD));
					}
				});
			}
		});
	}

	/**
	 * Load the UCDP/EAD submissions
	 */
	async loadData(): Promise<void> {
		this.loading = true;
		this.submissionSelected = new UcdpSumissionDataModel();

		try {
			const submissions = await lastValueFrom(this.qualityService.getUCDPSubmissions(this.orderDetail.id));
			this.ucdpData.submissions = submissions;
		} finally {
			this.loading = false;
		}
	}

	/**
	 * Load all the information related to an specific submissionId
	 */
	async loadSubmissionData(): Promise<void> {
		this.loadingSub = true;
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		const submissionId = this.submissionSelected.submissionId!; // '69a9a78d7d77b8adb830e5ec';

		try {
			const findings = await lastValueFrom(
				this.qualityService.getUCDPFindings(this.orderDetail.id, submissionId)
			);
			if (findings) {
				this.ucdpData.findings = findings.data;
			}
			const hardstops = await lastValueFrom(
				this.qualityService.getUCDPHardstops(this.orderDetail.id, submissionId)
			);
			if (hardstops) {
				this.ucdpData.hardstops = hardstops.data;
			}
		} finally {
			this.loadingSub = false;
		}
	}

	/**
	 * Open the Override Dialog
	 *
	 * @param overrideId
	 */
	openOverride(hardStop: UcdpHardstopsDataModel): void {
		const dialog = this.dialogService.open(UcdpOverrideDialogComponent, {
			header: 'Override Action',
			width: '600px',
			data: {
				orderDetail: this.orderDetail,
				submission: this.submissionSelected,
				hardStop
			}
		});

		dialog.onClose.pipe(takeUntil(this.atComponentDestroy)).subscribe((data) => {
			if (data?.reloadFindings) {
				this.loadSubmissionData();
			}
		});
	}

	/**
	 * Open the UCDPOverrideData Dialog
	 */
	openOverrideData(): void {
		const dialog = this.dialogService.open(UCDPScoreDialogComponent, {
			width: '960px',
			header: 'Set Manual UCDP/EAD Data',
			data: {
				orderId: this.orderDetail?.id,
				documentId: this.orderDetail?.documentId
			},
			styleClass: 'reopen-dialog ucdp-override-dialog'
		});

		dialog.onClose.pipe(takeUntil(this.atComponentDestroy)).subscribe((data) => {
			if (data?.reloadData) {
				this.loadData();
			}
		});
	}

	/**
	 * Open Submission ERror Dialog
	 *
	 * @param item @see UcdpSumissionDataModel
	 */
	openSubmissionError(item: UcdpSumissionDataModel): void {
		this.dialogService.open(UcdpSubmissionErrorDialogComponent, {
			width: '500px',
			data: {
				item
			},
			styleClass: 'details-dialog-container basic-modal'
		});
	}

	/**
	 * Pre-step to submit to UCDP service or to show a dialog with messages
	 * @param serviceName
	 */
	triggerSubmitAction(serviceName: string): void {
		let canSubmit = false;
		let messages;
		switch (serviceName) {
			case 'ucdp':
				canSubmit = this.ucdpData.submissions.canSubmitUCDP;
				messages = this.ucdpData.submissions.canSubmitUcdpMessages;
				break;
			case 'ead':
				canSubmit = this.ucdpData.submissions.canSubmitEAD;
				messages = this.ucdpData.submissions.canSubmitEadMessages;
				break;
		}

		if (canSubmit) {
			this.submitToService(serviceName);
		} else {
			this.basicModal.showSubmissionErrors = true;
			this.basicModal.serviceName = serviceName;
			this.basicModal.message = messages;
		}
	}

	/**
	 * Manually submit the document to the specified service
	 *
	 * @param serviceName
	 */
	submitToService(serviceName: string): void {
		this.loading = true;
		this.qualityService.postUCDPSubmission(this.orderDetail.id, serviceName).subscribe((data) => {
			if (data.isValid) {
				this.broadcastService.broadcast({
					name: ToasterType.SUCCESS,
					event: {
						message: 'Submited successfully'
					}
				});
				this.submissionSelected = new UcdpSumissionDataModel();
				this.loadData$.next(true);
			} else {
				this.loading = false;
			}
		});
	}

	/**
	 * Hit endpoint that will trigger the submit of all the overrides
	 */
	submitOverrides(): void {
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		const submissionId = this.submissionSelected.submissionId!; // '69a9a78d7d77b8adb830e5ec';
		this.qualityService.postOverridesRevision(this.orderDetail.id, submissionId).subscribe((data) => {
			if (data.isValid) {
				this.broadcastService.broadcast({
					name: ToasterType.SUCCESS,
					event: {
						message: 'Submited successfully'
					}
				});
				this.submissionSelected = new UcdpSumissionDataModel();
				this.loadData$.next(true);
			} else {
				this.loading = false;
			}
		});
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
